import { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import Loader from '../components/Loader';
import SearchableSelect from '../components/SearchableSelect';
import ViewToggle from '../components/ViewToggle';
import { cardHeaderHue } from '../utils/cardTheme';
import { useViewPreference } from '../utils/viewPreference';
import { PAGE_SIZE_OPTIONS, buildPageList } from '../utils/pagination';

const EMPTY_FORM = { level_code: '', level_name: '', sequence: '', is_active: 1 };
const SORT_OPTIONS = [
  { key: 'sequence', label: 'Sequence' },
  { key: 'level_code', label: 'Level Code' },
  { key: 'level_name', label: 'Level Name' },
  { key: 'is_active', label: 'Status' },
];

function compareValues(a, b) {
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

export default function CourseLevelPage() {
  const { show } = useToast();
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [perms, setPerms] = useState(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortKey, setSortKey] = useState('sequence');
  const [sortDir, setSortDir] = useState('asc');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [view, setView] = useViewPreference('course-levels');

  const [modalMode, setModalMode] = useState(null); // 'create' | 'edit' | null
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [viewRow, setViewRow] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get('/course-levels'),
      api.get('/users/me/permissions'),
    ])
      .then(([levelsRes, permsRes]) => {
        setLevels(levelsRes.data.data);
        setPerms(permsRes.data.data.course_level_master || { view: false, create: false, update: false, delete: false });
      })
      .catch((err) => setError(err.response?.data?.message || 'Failed to load course levels'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, statusFilter, pageSize]);

  const filteredSorted = useMemo(() => {
    const term = search.trim().toLowerCase();
    let list = levels.filter((l) => {
      if (term && !l.level_code.toLowerCase().includes(term) && !l.level_name.toLowerCase().includes(term)) return false;
      if (statusFilter === 'active' && !l.is_active) return false;
      if (statusFilter === 'inactive' && l.is_active) return false;
      return true;
    });
    if (sortKey) {
      const dir = sortDir === 'asc' ? 1 : -1;
      list = list.slice().sort((a, b) => compareValues(a[sortKey], b[sortKey]) * dir);
    }
    return list;
  }, [levels, search, statusFilter, sortKey, sortDir]);

  const total = filteredSorted.length;
  const effectivePageSize = pageSize === 'All' ? total || 1 : pageSize;
  const totalPages = Math.max(1, Math.ceil(total / effectivePageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = useMemo(() => {
    if (pageSize === 'All') return filteredSorted;
    const start = (currentPage - 1) * effectivePageSize;
    return filteredSorted.slice(start, start + effectivePageSize);
  }, [filteredSorted, currentPage, effectivePageSize, pageSize]);

  const rangeStart = total === 0 ? 0 : (pageSize === 'All' ? 1 : (currentPage - 1) * effectivePageSize + 1);
  const rangeEnd = pageSize === 'All' ? total : Math.min(currentPage * effectivePageSize, total);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormError(null);
    setModalMode('create');
  };

  const openEdit = (row) => {
    setForm({ level_code: row.level_code, level_name: row.level_name, sequence: row.sequence, is_active: row.is_active ? 1 : 0 });
    setFormError(null);
    setModalMode('edit');
  };

  const toggleActive = async (row) => {
    try {
      await api.put(`/course-levels/${row.level_code}`, {
        level_name: row.level_name,
        sequence: row.sequence,
        is_active: row.is_active ? 0 : 1,
      });
      show(`Course level ${row.is_active ? 'disabled' : 'enabled'}`, 'success');
      load();
    } catch (err) {
      show(err.response?.data?.message || 'Failed to update status', 'error');
    }
  };

  const closeModal = () => {
    setModalMode(null);
    setForm(EMPTY_FORM);
    setFormError(null);
  };

  const isDuplicateCode = useMemo(() => {
    if (modalMode !== 'create') return false;
    const code = form.level_code.trim().toLowerCase();
    if (!code) return false;
    return levels.some((l) => l.level_code.toLowerCase() === code);
  }, [modalMode, form.level_code, levels]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const codeBlank = !form.level_code || !form.level_code.trim();
    const nameBlank = !form.level_name || !form.level_name.trim();
    const seqBlank = form.sequence === '' || form.sequence === null || form.sequence === undefined;
    if (codeBlank || nameBlank || seqBlank) {
      setFormError('All fields are required.');
      return;
    }
    if (modalMode === 'create' && isDuplicateCode) {
      setFormError('Level code already exists.');
      return;
    }
    setFormError(null);
    setSubmitting(true);
    try {
      if (modalMode === 'create') {
        await api.post('/course-levels', {
          level_code: form.level_code.trim(),
          level_name: form.level_name.trim(),
          sequence: Number(form.sequence),
        });
        show('Course level created', 'success');
      } else {
        await api.put(`/course-levels/${form.level_code}`, {
          level_name: form.level_name.trim(),
          sequence: Number(form.sequence),
          is_active: Number(form.is_active),
        });
        show('Course level updated', 'success');
      }
      closeModal();
      load();
    } catch (err) {
      setFormError(err.response?.data?.message || `Failed to ${modalMode === 'create' ? 'create' : 'update'} course level`);
    } finally {
      setSubmitting(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
  };

  if (!loading && !error && perms && !perms.view) {
    return (
      <div>
        <div className="sh-ey">PROGRAMMES · COURSE LEVEL MASTER</div>
        <div className="sh-ti">Course Level Master</div>
        <div className="alert"><i className="fa-solid fa-lock" /><div className="alert-t">You do not have access to Course Level Master.</div></div>
      </div>
    );
  }

  return (
    <div>
      <div className="sh-ey">PROGRAMMES · COURSE LEVEL MASTER</div>
      <div className="sh-ti">Course Level Master</div>
      <div className="sh-de">Live course level catalogue from the backend — used to group and order courses across the programme portfolio.</div>

      <div style={{ display: 'flex', gap: '.75rem', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div className="icon-field" style={{ flex: '1 1 240px', minWidth: 200 }}>
          <i className="fa-solid fa-magnifying-glass" />
          <input className="finput" placeholder="Search level code or name…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="icon-field">
          <i className="fa-solid fa-filter" />
          <SearchableSelect
            className="finput" style={{ width: 170 }} value={statusFilter} onChange={setStatusFilter}
            placeholder="All Statuses"
            options={[{ value: '', label: 'All Statuses' }, { value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]}
          />
        </div>
        {view === 'tiles' && (
          <div className="icon-field" style={{ flex: '1 1 180px', minWidth: 160 }}>
            <i className="fa-solid fa-arrow-down-a-z" />
            <SearchableSelect
              className="finput" value={sortKey} onChange={setSortKey}
              options={SORT_OPTIONS.map((o) => ({ value: o.key, label: `Sort: ${o.label}` }))}
            />
          </div>
        )}
        {view === 'tiles' && (
          <button type="button" className="btn btn-outline btn-toolbar" title={sortDir === 'asc' ? 'Ascending' : 'Descending'} onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}>
            <i className={`fa-solid ${sortDir === 'asc' ? 'fa-arrow-up-wide-short' : 'fa-arrow-down-wide-short'}`} />
          </button>
        )}
        <button type="button" className="btn btn-outline btn-toolbar" onClick={clearFilters}><i className="fa-solid fa-arrow-rotate-left" /> Clear</button>
        <ViewToggle view={view} onChange={setView} />
        {perms?.create && (
          <button className="btn btn-primary btn-toolbar" style={{ marginLeft: 'auto' }} onClick={openCreate}><i className="fa-solid fa-plus" /> Add Course Level</button>
        )}
      </div>

      {loading && <Loader label="Loading course levels…" />}
      {error && !loading && <div className="alert"><i className="fa-solid fa-triangle-exclamation" /><div className="alert-t">{error}</div></div>}

      {!loading && !error && !levels.length && (
        <div className="card" style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
          <i className="fa-solid fa-layer-group" style={{ fontSize: '2rem', color: 'var(--slate-lt)', marginBottom: '.75rem', display: 'block' }} />
          <div style={{ fontWeight: 700, color: 'var(--ink)', marginBottom: '.35rem' }}>No course levels yet</div>
          <div style={{ color: 'var(--slate)', marginBottom: '1rem' }}>Add your first course level to get started.</div>
          {perms?.create && (
            <button className="btn btn-primary" style={{ margin: '0 auto' }} onClick={openCreate}><i className="fa-solid fa-plus" /> Add Course Level</button>
          )}
        </div>
      )}

      {!loading && !error && !!levels.length && !total && (
        <div className="card" style={{ textAlign: 'center', padding: '2.5rem 1rem', color: 'var(--slate)' }}>
          No course levels match the current search/filters.
        </div>
      )}

      {!loading && !error && !!total && (
        <div>
          {view === 'tiles' && (
            <div className="g4 view-fade" key="tiles">
              {pageRows.map((row) => (
                <div key={row.level_code} className="cc-card" style={{ cursor: 'pointer' }} onClick={() => setViewRow(row)}>
                  <div className="cc-hd cc-hd-tint" style={{ '--h': cardHeaderHue(row.level_code) }}>
                    <div className="cc-em"><i className="fa-solid fa-layer-group" /></div>
                    <div style={{ minWidth: 0 }}>
                      <div className="cc-code">{row.level_code}</div>
                      <div className="cc-ti">{row.level_name}</div>
                    </div>
                  </div>
                  <div className="cc-body">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '.4rem', fontSize: 'var(--text-sm)', color: 'var(--slate)' }}>
                      <i className="fa-solid fa-arrow-up-1-9" style={{ color: 'var(--slate-lt)' }} /> Sequence <strong style={{ color: 'var(--ink-s)' }}>{row.sequence}</strong>
                    </div>
                  </div>
                  <div className="cc-foot">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }} onClick={(e) => e.stopPropagation()}>
                      {perms?.update ? (
                        <button
                          type="button"
                          className={`toggle ${row.is_active ? 'on' : ''}`}
                          role="switch"
                          aria-checked={!!row.is_active}
                          title={row.is_active ? 'Disable course level' : 'Enable course level'}
                          aria-label={row.is_active ? 'Disable course level' : 'Enable course level'}
                          onClick={() => toggleActive(row)}
                        />
                      ) : null}
                      <span className={`pill ${row.is_active ? 'p-ok' : 'p-draft'}`}><i className={`fa-solid ${row.is_active ? 'fa-circle-check' : 'fa-circle-minus'}`} /> {row.is_active ? 'Active' : 'Inactive'}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '.4rem' }} onClick={(e) => e.stopPropagation()}>
                      {perms?.update && (
                        <button type="button" className="btn btn-outline btn-sm" title="Edit course level" aria-label="Edit course level" onClick={() => openEdit(row)}><i className="fa-solid fa-pen" /></button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {view === 'table' && (
            <div className="card view-fade" key="table">
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Name</th>
                      <th>Sequence</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((row) => (
                      <tr key={row.level_code} style={{ cursor: 'pointer' }} onClick={() => setViewRow(row)}>
                        <td><span style={{ fontFamily: 'var(--font-mono)', fontSize: '.8rem', fontWeight: 700, color: 'var(--ocean)' }}>{row.level_code}</span></td>
                        <td style={{ fontWeight: 600 }}>{row.level_name}</td>
                        <td>{row.sequence}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                            {perms?.update ? (
                              <button
                                type="button"
                                className={`toggle ${row.is_active ? 'on' : ''}`}
                                role="switch"
                                aria-checked={!!row.is_active}
                                title={row.is_active ? 'Disable course level' : 'Enable course level'}
                                aria-label={row.is_active ? 'Disable course level' : 'Enable course level'}
                                onClick={() => toggleActive(row)}
                              />
                            ) : null}
                            <span className={`pill ${row.is_active ? 'p-ok' : 'p-draft'}`}><i className={`fa-solid ${row.is_active ? 'fa-circle-check' : 'fa-circle-minus'}`} /> {row.is_active ? 'Active' : 'Inactive'}</span>
                          </div>
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          {perms?.update && (
                            <button type="button" className="btn btn-outline btn-sm" title="Edit course level" aria-label="Edit course level" onClick={() => openEdit(row)}><i className="fa-solid fa-pen" /></button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginTop: '1.3rem', paddingTop: '1rem', borderTop: '1px solid var(--border-soft)' }}>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--slate)' }}>
              Showing {rangeStart}–{rangeEnd} of {total}
            </div>
            <div className="icon-field" style={{ marginLeft: 'auto' }}>
              <SearchableSelect
                className="finput" style={{ width: 110 }} value={pageSize}
                onChange={(val) => setPageSize(val === 'All' ? 'All' : Number(val))}
                options={PAGE_SIZE_OPTIONS.map((opt) => ({ value: opt, label: String(opt) }))}
              />
            </div>
            {pageSize !== 'All' && totalPages > 1 && (
              <div style={{ display: 'flex', gap: '.3rem', alignItems: 'center' }}>
                <button type="button" className="btn btn-ghost btn-sm" disabled={currentPage === 1} onClick={() => setPage(1)}><i className="fa-solid fa-angles-left" /></button>
                <button type="button" className="btn btn-ghost btn-sm" disabled={currentPage === 1} onClick={() => setPage((p) => p - 1)}><i className="fa-solid fa-angle-left" /></button>
                {buildPageList(currentPage, totalPages).map((p, idx) => (
                  p === '…'
                    ? <span key={`e${idx}`} style={{ padding: '0 .3rem', color: 'var(--slate-lt)' }}>…</span>
                    : (
                      <button
                        key={p}
                        type="button"
                        className={`btn btn-sm ${p === currentPage ? 'btn-primary' : 'btn-ghost'}`}
                        onClick={() => setPage(p)}
                      >
                        {p}
                      </button>
                    )
                ))}
                <button type="button" className="btn btn-ghost btn-sm" disabled={currentPage === totalPages} onClick={() => setPage((p) => p + 1)}><i className="fa-solid fa-angle-right" /></button>
                <button type="button" className="btn btn-ghost btn-sm" disabled={currentPage === totalPages} onClick={() => setPage(totalPages)}><i className="fa-solid fa-angles-right" /></button>
              </div>
            )}
          </div>
        </div>
      )}

      {modalMode && (
        <div className="sb-backdrop" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={closeModal}>
          <form className="card modal" style={{ width: 460, maxWidth: '92vw' }} onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
            <div className="modal-header">
              <div className="modal-title">{modalMode === 'create' ? 'Add Course Level' : 'Edit Course Level'}</div>
              <button type="button" className="modal-close" onClick={closeModal} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
            </div>
            <div className="modal-body">
            {formError && <div className="lc-err">{formError}</div>}
            <div className="fg fg-tight">
              <label className="fl">Level Code *</label>
              <input
                className="finput"
                required
                disabled={modalMode === 'edit'}
                value={form.level_code}
                onChange={(e) => setForm((f) => ({ ...f, level_code: e.target.value }))}
              />
              {modalMode === 'edit' && (
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--slate)', marginTop: '.25rem' }}>Level code cannot be changed after creation</div>
              )}
              {modalMode === 'create' && isDuplicateCode && (
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--red)', marginTop: '.25rem' }}>Level code already exists.</div>
              )}
            </div>
            <div className="fg fg-tight"><label className="fl">Level Name *</label><input className="finput" required value={form.level_name} onChange={(e) => setForm((f) => ({ ...f, level_name: e.target.value }))} /></div>
            <div className="fg fg-tight"><label className="fl">Sequence *</label><input className="finput" type="number" required value={form.sequence} onChange={(e) => setForm((f) => ({ ...f, sequence: e.target.value }))} /></div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={closeModal}><i className="fa-solid fa-xmark" /> Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting || (modalMode === 'create' && isDuplicateCode)}>
                <i className={`fa-solid ${submitting ? 'fa-spinner fa-spin' : 'fa-check'}`} />
                {submitting ? 'Saving…' : modalMode === 'create' ? 'Create Course Level' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      )}

      {viewRow && (
        <div className="sb-backdrop" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setViewRow(null)}>
          <div className="card modal" style={{ width: 460, maxWidth: '92vw' }} onClick={(e) => e.stopPropagation()}>
            <div className="vw-hd">
              <div className="vw-em"><i className="fa-solid fa-layer-group" /></div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 'var(--text-xs)', color: 'var(--ocean)', letterSpacing: '.04em' }}>{viewRow.level_code}</div>
                <div style={{ fontWeight: 700, fontSize: 'var(--text-lg)', color: 'var(--ink)' }}>{viewRow.level_name}</div>
              </div>
              <span className={`pill ${viewRow.is_active ? 'p-ok' : 'p-draft'}`} style={{ marginLeft: 'auto', flexShrink: 0 }}><i className={`fa-solid ${viewRow.is_active ? 'fa-circle-check' : 'fa-circle-minus'}`} /> {viewRow.is_active ? 'Active' : 'Inactive'}</span>
              <button type="button" className="modal-close" onClick={() => setViewRow(null)} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
            </div>
            <div className="modal-body">
              <div className="vw-grid">
                <div className="fg"><label className="fl">Sequence</label><div className="vw-val">{viewRow.sequence}</div></div>
                <div className="fg"><label className="fl">Status</label><div className="vw-val"><span className={`pill ${viewRow.is_active ? 'p-ok' : 'p-draft'}`}><i className={`fa-solid ${viewRow.is_active ? 'fa-circle-check' : 'fa-circle-minus'}`} /> {viewRow.is_active ? 'Active' : 'Inactive'}</span></div></div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={() => setViewRow(null)}>Close</button>
              {perms?.update && (
                <button type="button" className="btn btn-primary" onClick={() => { setViewRow(null); openEdit(viewRow); }}><i className="fa-solid fa-pen" /> Edit Level</button>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
